import type { QuestionnaireResponse } from '@scripsteam/scrips-signal-ds';

/**
 * PROD-3183 (ROS, PE and risk part) — "is this answer in the stored note yet?", as a promise.
 *
 * An accepted finding is written into the note's one QuestionnaireResponse and persisted by the
 * note's own autosave (`handleSave`, the proven path: no second persist shape). The accept is
 * recorded as Written only once a save that CARRIED the answer succeeded, the same rule the A&P
 * accepts follow (`whenPersisted` / `whenLineSaved` on the page).
 *
 * Each waiter holds a test (`carries`) and settles:
 *   true  — a save whose body carried the answer succeeded (`saved`);
 *   false — a save failed (`failed`), or the answer left the note before any save carried it
 *           (`current`: the clinician removed it, or Discard cleared the note).
 * Pure. No React.
 */
/**
 * How long an accepted finding waits for a save that carries it. The note autosaves 1.8 s after an
 * edit and a save normally answers in a second or two, so 20 s means the save never started (the
 * note stopped saving) or never answered. The waiter then settles false: the item says "not saved
 * yet" and stays actionable, instead of spinning for ever. The answer stays in the note; if a save
 * carries it later, "Try again" finds it stored and records the accept.
 */
export const QR_SAVE_WAIT_MS = 20_000;

export interface QrSaveWaiters {
  /**
   * Settles when a save carrying the answer succeeds (true) or the answer cannot arrive (false):
   * a failed save, the answer left the note, or `timeoutMs` passed with no save carrying it.
   */
  whenCarried(carries: (response: QuestionnaireResponse) => boolean, timeoutMs?: number): Promise<boolean>;
  /** A save of `sent` succeeded. */
  saved(sent: QuestionnaireResponse): void;
  /** A save failed. Every waiter settles false; the item says "not saved" and can be tried again. */
  failed(): void;
  /** The note changed. A waiter whose answer is no longer in it settles false. */
  current(now: QuestionnaireResponse): void;
  /** How many are waiting (tests). */
  readonly size: number;
}

interface Waiter {
  carries: (response: QuestionnaireResponse) => boolean;
  resolve: (stored: boolean) => void;
  timer?: ReturnType<typeof setTimeout>;
}

export function createQrSaveWaiters(): QrSaveWaiters {
  let waiting: Waiter[] = [];
  const settle = (pick: (w: Waiter) => boolean, stored: boolean) => {
    const done = waiting.filter(pick);
    if (done.length === 0) return;
    waiting = waiting.filter((w) => !done.includes(w));
    done.forEach((w) => {
      if (w.timer !== undefined) clearTimeout(w.timer);
      w.resolve(stored);
    });
  };
  return {
    whenCarried: (carries, timeoutMs = QR_SAVE_WAIT_MS) =>
      new Promise<boolean>((resolve) => {
        const waiter: Waiter = { carries, resolve };
        waiter.timer = setTimeout(() => settle((w) => w === waiter, false), timeoutMs);
        waiting = [...waiting, waiter];
      }),
    saved: (sent) => settle((w) => w.carries(sent), true),
    failed: () => settle(() => true, false),
    current: (now) => settle((w) => !w.carries(now), false),
    get size() {
      return waiting.length;
    },
  };
}
